class CustomImageSliderWithText extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    this.slides = Array.from(this.querySelectorAll('[data-slider-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-slider-dot]'));
    this.index = 0;
    this.startX = null;
    this.autoplayTimer = null;

    if (!this.track || this.slides.length === 0) return;

    this.dots.forEach((dot) => dot.addEventListener('click', this.selectDot));
    this.track.addEventListener('pointerdown', this.pointerDown);
    this.track.addEventListener('pointerup', this.pointerUp);
    this.track.addEventListener('pointercancel', this.pointerCancel);
    this.addEventListener('mouseenter', this.stopAutoplay);
    this.addEventListener('mouseleave', this.startAutoplay);
    this.addEventListener('focusin', this.stopAutoplay);
    this.addEventListener('focusout', this.startAutoplay);

    this.update();
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.dots.forEach((dot) => dot.removeEventListener('click', this.selectDot));
    this.track?.removeEventListener('pointerdown', this.pointerDown);
    this.track?.removeEventListener('pointerup', this.pointerUp);
    this.track?.removeEventListener('pointercancel', this.pointerCancel);
    this.removeEventListener('mouseenter', this.stopAutoplay);
    this.removeEventListener('mouseleave', this.startAutoplay);
    this.removeEventListener('focusin', this.stopAutoplay);
    this.removeEventListener('focusout', this.startAutoplay);
    this.stopAutoplay();
  }

  get loop() {
    return this.dataset.loop === 'true';
  }

  previous = () => this.goTo(this.index - 1);
  next = () => this.goTo(this.index + 1);

  selectDot = (event) => {
    this.goTo(Number(event.currentTarget.dataset.sliderDot));
  };

  goTo(nextIndex) {
    if (this.slides.length < 2) return;

    if (this.loop) {
      this.index = (nextIndex + this.slides.length) % this.slides.length;
    } else {
      this.index = Math.min(Math.max(nextIndex, 0), this.slides.length - 1);
    }

    this.update();
  }

  update() {
    this.style.setProperty('--custom-image-slider-index', this.index);

    this.slides.forEach((slide, index) => {
      const active = index === this.index;
      slide.setAttribute('aria-label', `${index + 1} of ${this.slides.length}`);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    this.dots.forEach((dot, index) => {
      const active = index === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });

  }

  pointerDown = (event) => {
    if (this.slides.length < 2 || event.pointerType === 'mouse' && event.button !== 0) return;
    this.startX = event.clientX;
    this.track.setPointerCapture?.(event.pointerId);
  };

  pointerUp = (event) => {
    if (this.startX === null) return;
    const distance = event.clientX - this.startX;
    this.startX = null;

    if (Math.abs(distance) < 45) return;
    if (distance < 0) this.next();
    else this.previous();
  };

  pointerCancel = () => {
    this.startX = null;
  };

  startAutoplay = () => {
    this.stopAutoplay();
    const delay = Number(this.dataset.autoplay);
    if (!delay || this.slides.length < 2) return;
    this.autoplayTimer = window.setInterval(this.next, delay);
  };

  stopAutoplay = () => {
    if (this.autoplayTimer) window.clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  };
}

if (!customElements.get('custom-image-slider-with-text')) {
  customElements.define('custom-image-slider-with-text', CustomImageSliderWithText);
}

class ScrollToProductsButton extends HTMLElement {
  connectedCallback() {
    this.link = this.querySelector('a[href="#add-to-cart-products"]');
    this.link?.addEventListener('click', this.scrollToProducts);
  }

  disconnectedCallback() {
    this.link?.removeEventListener('click', this.scrollToProducts);
  }

  scrollToProducts = (event) => {
    const target = document.querySelector('#add-to-cart-products');
    if (!target) return;

    event.preventDefault();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({
      behavior: this.dataset.smoothScroll === 'true' && !reduceMotion ? 'smooth' : 'auto',
      block: 'start',
    });
    window.history.replaceState(null, '', '#add-to-cart-products');
  };
}

if (!customElements.get('scroll-to-products-button')) {
  customElements.define('scroll-to-products-button', ScrollToProductsButton);
}
